import React, { useState, useCallback } from 'react';
import { Box, FormControl, FormHelperText, Grid2 as Grid, Card, CardContent, Typography, Tooltip } from '@mui/material';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { isDevEnvironment } from '../../utils/environment';

const STATIC_CDN_DEV = 'https://static.dev.agendario.com.br';
const STATIC_CDN_PROD = 'https://static.agendario.com.br';
const CDN_BASE = isDevEnvironment() ? STATIC_CDN_DEV : STATIC_CDN_PROD;

const getFullPreviewUrl = (relativeUrl) => {
  if (!relativeUrl) return '';
  if (relativeUrl.startsWith('http://') || relativeUrl.startsWith('https://')) return relativeUrl;
  return CDN_BASE + relativeUrl;
};

/**
 * TemplateSelector - Seletor visual de templates com preview
 * Mostra miniaturas de cada template permitindo seleção via checkbox visual
 * Converte URLs relativas de preview para URLs completas baseado no ambiente
 */
export const TemplateSelector = ({ templates = [], value, onChange, error }) => {
  // Rastreia quais URLs de preview falharam; renderiza placeholder via JSX (sem DOM manipulation)
  // para evitar loops infinitos causados por re-renders do componente pai.
  const [failedUrls, setFailedUrls] = useState(new Set());

  const handleImageError = useCallback((relativeUrl) => {
    setFailedUrls((prev) => {
      if (prev.has(relativeUrl)) return prev; // já registrado, sem novo state
      const next = new Set(prev);
      next.add(relativeUrl);
      return next;
    });
  }, []);

  if (!templates || templates.length === 0) {
    return (
      <FormControl fullWidth error={!!error}>
        <Typography variant="body2" color="textSecondary">
          Nenhum template disponível
        </Typography>
      </FormControl>
    );
  }

  const handleSelect = (templateId) => {
    onChange(templateId);
  };

  return (
    <FormControl fullWidth error={!!error}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Escolha um Template
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 3 }}>
          Selecione o layout que melhor se adequa ao seu negócio
        </Typography>
      </Box>

      <Grid container spacing={2}>
        {templates.map((template) => {
          const isSelected = value === template.id;
          const Icon = isSelected ? CheckCircleIcon : RadioButtonUncheckedIcon;
          const imageAlt = template.description ? `${template.name} - ${template.description}` : template.name;

          return (
            <Grid size={{ xs: 12, sm: 6, md: 3 }} key={template.id}>
              <Tooltip 
                title={template.description || template.name}
                placement="top"
              >
                <Card
                  onClick={() => handleSelect(template.id)}
                  sx={{
                    cursor: 'pointer',
                    position: 'relative',
                    border: (theme) => isSelected ? `3px solid ${theme.palette.primary.main}` : `2px solid ${theme.palette.grey[300]}`,
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    backgroundColor: (theme) => isSelected ? theme.palette.action.selected : theme.palette.background.paper,
                    '&:hover': {
                      borderColor: (theme) => theme.palette.primary.main,
                      boxShadow: (theme) => theme.shadows[3],
                      transform: 'translateY(-2px)',
                    },
                  }}
                >
                  {/* Checkbox no canto superior direito */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      zIndex: 10,
                      color: (theme) => isSelected ? theme.palette.primary.main : theme.palette.text.disabled,
                    }}
                  >
                    <Icon sx={{ fontSize: 28, fontWeight: 'bold' }} />
                  </Box>

                  {/* Preview da imagem */}
                  <Box
                    sx={(theme) => ({
                      width: '100%',
                      height: 140,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: theme.palette.grey[100],
                      borderBottom: `1px solid ${theme.palette.divider}`,
                    })}
                  >
                    {template.previewImageUrl && !failedUrls.has(template.previewImageUrl) ? (
                      <Box
                        component="img"
                        src={getFullPreviewUrl(template.previewImageUrl)}
                        alt={imageAlt}
                        onError={() => handleImageError(template.previewImageUrl)}
                        sx={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain',
                          display: 'block',
                        }}
                      />
                    ) : (
                      <BrokenImageIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                    )}
                  </Box>

                  {/* Nome */}
                  <CardContent sx={{ py: 1.25 }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: 600,
                        fontSize: '1rem',
                        mb: 0,
                        color: (theme) => isSelected ? theme.palette.primary.main : theme.palette.text.primary,
                      }}
                    >
                      {template.name}
                    </Typography>
                  </CardContent>
                </Card>
              </Tooltip>
            </Grid>
          );
        })}
      </Grid>

      {error && (
        <FormHelperText sx={{ mt: 2, color: '#d32f2f' }}>
          {error}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default TemplateSelector;
